<template>
  <v-form ref="form" class="access-form" @submit.prevent="submit">
    <div class="access-form__grid">
      <rmr-text-field
        v-model="formData.name"
        label="Nom"
        :rules="[required]"
      />
      <rmr-text-field
        v-model="formData.email"
        label="Email"
        :rules="[required]"
      />
      <rmr-text-field
        v-model="formData.home_page_url"
        label="Page d'accueil"
        :rules="[required]"
      />

      <div class="access-form__password">
        <rmr-text-field
          v-model="formData.password"
          label="Mot de passe"
          :rules="[passwordRule]"
        />
        <rmr-action-button
          label="Générer"
          variant="transparent"
          size="sm"
          @click="generatePassword()"
        />
      </div>

      <rmr-autocomplete
        v-model="formData.role_ids"
        :items="roles"
        item-text="name"
        item-value="id"
        label="Rôles"
        multiple
        chips
        deletable-chips
        :rules="[rolesSelected]"
      />

      <rmr-toggle-card
        v-model="formData.is_site_restricted"
        label="Restreindre l'accès à certains sites"
      />

      <rmr-autocomplete
        v-if="formData.is_site_restricted"
        v-model="formData.site_group_ids"
        :items="siteGroups"
        item-text="name"
        item-value="id"
        label="Univers (ajoute leurs sites à la sélection)"
        multiple
        chips
        deletable-chips
      />

      <rmr-autocomplete
        v-if="formData.is_site_restricted"
        v-model="formData.site_ids"
        :items="sites"
        item-text="name"
        item-value="id"
        label="Sites autorisés"
        multiple
        chips
        deletable-chips
      />

      <rmr-autocomplete
        v-model="formData.capabilities"
        :items="availableCapabilities"
        item-text="label"
        item-value="key"
        label="Capabilities (comportements activés)"
        multiple
        chips
        deletable-chips
      />
    </div>

    <div class="access-form__actions">
      <rmr-action-button
        label="Annuler"
        variant="ghost"
        @click="$emit('cancel')"
      />
      <rmr-action-button
        :label="editing ? `Mettre à jour` : `Créer l'utilisateur`"
        :loading="saving"
        type="submit"
      />
    </div>
  </v-form>
</template>

<script>
import { required } from "@core/utils/validation";
import axios from "@axios";
import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrAutocomplete from "@/components/ui/forms/RmrAutocomplete.vue";
import RmrTextField from "@/components/ui/forms/RmrTextField.vue";
import RmrToggleCard from "@/components/ui/forms/RmrToggleCard.vue";

const AVAILABLE_CAPABILITIES = [
  { key: "editorialink_leparisien", label: "Externe - Le Parisien (EditoriaLink)" },
];

function capabilitiesToList(capabilities) {
  if (!capabilities || typeof capabilities !== "object") {
    return [];
  }

  return Object.keys(capabilities).filter((key) => capabilities[key]);
}

function buildInitialState(value) {
  return {
    id: value?.id || null,
    name: value?.name || "",
    email: value?.email || "",
    password: "",
    home_page_url: value?.home_page_url || "",
    role_ids: [...(value?.role_ids || [])],
    is_site_restricted: value?.is_site_restricted || false,
    site_ids: [...(value?.site_ids || [])],
    site_group_ids: [],
    capabilities: capabilitiesToList(value?.capabilities),
  };
}

export default {
  name: "UserForm",
  components: {
    RmrActionButton,
    RmrAutocomplete,
    RmrTextField,
    RmrToggleCard,
  },
  props: {
    value: {
      type: Object,
      default: null,
    },
    roles: {
      type: Array,
      default: () => [],
    },
    sites: {
      type: Array,
      default: () => [],
    },
    siteGroups: {
      type: Array,
      default: () => [],
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      required,
      availableCapabilities: AVAILABLE_CAPABILITIES,
      formData: buildInitialState(this.value),
    };
  },
  computed: {
    editing() {
      return Boolean(this.value && this.value.id);
    },
  },
  watch: {
    value: {
      immediate: false,
      deep: true,
      handler(nextValue) {
        this.formData = buildInitialState(nextValue);
        this.$nextTick(() => this.$refs.form?.resetValidation());
      },
    },
    "formData.site_group_ids": {
      handler(groupIds) {
        this.addSitesFromUnivers(groupIds);
      },
    },
  },
  methods: {
    async addSitesFromUnivers(groupIds) {
      if (!groupIds || !groupIds.length) {
        return;
      }
      const { data } = await axios.get(
        `/site-groups/sites?group_ids=${groupIds.join(",")}`
      );
      const siteIds = (data || []).map((row) => row.site_id);
      this.formData.site_ids = [
        ...new Set([...this.formData.site_ids, ...siteIds]),
      ];
    },
    passwordRule(value) {
      return this.editing ? true : required(value);
    },
    rolesSelected(value) {
      return value && value.length ? true : "Ce champ est obligatoire";
    },
    generatePassword(length = 12) {
      const characters =
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
      this.formData.password = Array.from(
        { length },
        () => characters[Math.floor(Math.random() * characters.length)]
      ).join("");
    },
    submit() {
      if (!this.$refs.form.validate()) {
        return;
      }

      const capabilities = (this.formData.capabilities || []).reduce(
        (acc, key) => ({ ...acc, [key]: true }),
        {}
      );

      const payload = {
        name: this.formData.name,
        email: this.formData.email,
        home_page_url: this.formData.home_page_url,
        role_ids: this.formData.role_ids,
        is_site_restricted: this.formData.is_site_restricted,
        site_ids: this.formData.is_site_restricted ? this.formData.site_ids : [],
        capabilities,
      };

      if (this.formData.password) {
        payload.password = this.formData.password;
      }

      this.$emit("save", payload);
    },
  },
};
</script>

<style lang="scss" scoped>
.access-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.access-form__grid {
  display: grid;
  gap: 16px;
}

.access-form__password {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: start;
}

.access-form__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

@media (max-width: 680px) {
  .access-form__password {
    grid-template-columns: 1fr;
  }

  .access-form__actions {
    flex-direction: column-reverse;
  }
}
</style>
